/**
 * Shared helpers for resolving Reviews.io config from the DOM.
 */

const REVIEWS_CONFIG_SELECTOR = 'script.js-reviews-config';

/**
 * Parse all valid Reviews.io config script tags.
 * @returns {Array<Object>} Parsed config objects in DOM order.
 */
export function getReviewsConfigs() {
  const scripts = document.querySelectorAll(REVIEWS_CONFIG_SELECTOR);
  const configs = [];

  for (const script of scripts) {
    if (!script.textContent) {
      continue;
    }

    try {
      const config = JSON.parse(script.textContent);
      configs.push(config);
    } catch (error) {
      console.warn('ReviewsConfig: Failed to parse one reviews config block:', error);
    }
  }

  return configs;
}

/**
 * Return the most useful reviews config:
 * - Prefer one with non-empty sku.
 * - Fallback to first parseable config.
 * @returns {Object|null} Preferred config object.
 */
export function getPreferredReviewsConfig() {
  const configs = getReviewsConfigs();

  if (configs.length === 0) {
    return null;
  }

  for (const config of configs) {
    const sku = typeof config?.sku === 'string' ? config.sku.trim() : '';
    if (sku) {
      return config;
    }
  }

  return configs[0] || null;
}

/**
 * Resolve preferred Reviews.io store key.
 * - Prefer store from config with non-empty sku.
 * - Fallback to first valid store found.
 * @returns {string} Store key or empty string.
 */
export function getPreferredReviewsStoreKey() {
  const config = getPreferredReviewsConfig();
  if (typeof config?.store === 'string' && config.store.trim() !== '') {
    return config.store;
  }

  return '';
}

/**
 * Resolve first non-empty SKU from preferred config.
 * @returns {string|null} First SKU token or null.
 */
export function getPreferredReviewsSku() {
  const config = getPreferredReviewsConfig();
  if (typeof config?.sku !== 'string' || config.sku.trim() === '') {
    return null;
  }

  const skus = config.sku.split(';').filter((sku) => sku.trim() !== '');
  return skus.length > 0 ? skus[0].trim() : null;
}
